import { MAX_FRACTIONAL_DIGITS_ALLOWED, roundToPrecision } from 'src/utils/ag-grid-utils';
import { ForecastGridFixedFields } from './ForecastGridConstants';

/**
 * Field-level normalizer used by the "did this field change vs the system?" comparators in the
 * upload pipeline. Consolidates the near-identical copies that lived in
 * `NonEditableFieldsValidationService` and `FileChangeDetectionValidationService`.
 * (`DataTransformationService.normalizeFieldValues` still carries its own variant — its
 * asymmetric nullification changes which rows count as "modified", so migrating it is a
 * separate, behavior-affecting change; see the TODO on that method.) Divergence between these
 * copies was flagged in CR review as the exact class of bug that produces false round-trip
 * violations — Excel + SheetJS silently flip types on a clean round-trip, so both sides of a
 * compare must go through the same rules.
 *
 * Strategy — three field classes, everything else defaults to string:
 *   - `is_planned_in_usd` — strict boolean. `null`/`undefined` template values coerce to `false`
 *     so a null-template + false-file pair (Excel export writes `null` as `false`) compares equal.
 *   - forecast month columns — parsed as float, rounded to the system-wide precision cap. Excel
 *     round-trips introduce IEEE-754 trailing noise on floats; rounding to
 *     MAX_FRACTIONAL_DIGITS_ALLOWED keeps both sides comparable to what the grid stores on load.
 *   - everything else — string-coerced. Covers corp segments, business segments, account codes,
 *     and any future ID-shaped mandatory field the template stores as string but SheetJS reads
 *     back as number. Defaulting to string beats enumerating field lists that inevitably fall
 *     out of date.
 *
 * Empty string / undefined / null nullify to `null` on both sides symmetrically. The asymmetric
 * nullification that used to live in `DataTransformationService.normalizeFieldValues` is
 * intentionally NOT preserved — see that file's TODO for the false-positive risk it created.
 */
export const normalizeFieldForComparison = (
  rawValue: unknown,
  fieldName: string,
  options: { forecastMonthCols: ReadonlySet<string> }
): unknown => {
  const nullified = rawValue === '' || rawValue === undefined || rawValue === null ? null : rawValue;

  if (fieldName === ForecastGridFixedFields.IS_PLANNED_IN_USD.value) {
    return nullified === true;
  }

  if (options.forecastMonthCols.has(fieldName)) {
    if (nullified === null) return null;
    const parsed = parseFloat(`${nullified}`);
    if (typeof parsed === 'number' && !isNaN(parsed)) return roundToPrecision(parsed, MAX_FRACTIONAL_DIGITS_ALLOWED);
    return parsed;
  }

  return nullified === null ? null : `${nullified}`;
};

/** Convenience wrapper — normalizes both sides of a compare in one call. */
export const normalizePairForComparison = (
  rawFileValue: unknown,
  rawSystemValue: unknown,
  fieldName: string,
  options: { forecastMonthCols: ReadonlySet<string> }
): { fileValue: unknown; systemValue: unknown } => ({
  fileValue: normalizeFieldForComparison(rawFileValue, fieldName, options),
  systemValue: normalizeFieldForComparison(rawSystemValue, fieldName, options)
});
